import React from 'react';
import { Subscribe } from 'unstated';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Stack, xcss } from '@atlaskit/primitives';

import {
	ContentStateStateContainer,
	type EditorContentStateQueryType,
} from '@confluence/content-state';
import { fg } from '@confluence/feature-gating';
import { useContentType } from '@confluence/page-context';
import { ContentType } from '@confluence/content-types-utils';

import { useElementVisibility, ELEMENTS } from '../hooks/useIsElementVisible';
import { useClassificationVisibility } from '../hooks/useClassificationVisibility';

import { StatusRow } from './StatusRow';
import { ClassificationRow } from './ClassificationRow';
import { LabelsSection } from './LabelsSection';
import { RedactionsRow } from './RedactionsRow';

const i18n = defineMessages({
	propertiesSectionTitle: {
		id: 'details-panel.info-section.title',
		defaultMessage: 'Info',
		description:
			'Title for the section containing info related to content (e.g. status, classification, labels)',
	},
});

const propertiesSectionStyles = xcss({
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	marginTop: 'space.300',
});

type ContentState = {
	state: EditorContentStateQueryType['singleContent'];
};

export const InfoSection = () => {
	const [contentType] = useContentType();
	const { isElementVisible } = useElementVisibility();
	const isClassificationRowVisible = useClassificationVisibility();

	const rowsToRender = [];

	if (isElementVisible(ELEMENTS.ROWS.STATUS)) {
		rowsToRender.push(
			<Subscribe to={[ContentStateStateContainer]} key={ELEMENTS.ROWS.STATUS}>
				{({ state }: ContentState) => {
					const { name = null, restrictionLevel, color = null } = state?.contentState ?? {};
					return (
						<StatusRow
							name={name}
							restrictionLevel={restrictionLevel}
							color={color}
							isContentStateAvailable={!!state?.contentState}
						/>
					);
				}}
			</Subscribe>,
		);
	}

	if (isClassificationRowVisible) {
		rowsToRender.push(<ClassificationRow key="classification" />);
	}
	if (isElementVisible(ELEMENTS.ROWS.REDACTIONS) && fg('dlp_cc-redactions-metadata-modal')) {
		rowsToRender.push(<RedactionsRow key={ELEMENTS.ROWS.REDACTIONS} />);
	}
	if (isElementVisible(ELEMENTS.ROWS.LABELS)) {
		rowsToRender.push(<LabelsSection key={ELEMENTS.ROWS.LABELS} />);
	}

	let isInfoSectionTitleVisible = true;
	if (
		contentType === ContentType.WHITEBOARD &&
		fg('confluence_frontend_whiteboards_content_wrapper')
	) {
		isInfoSectionTitleVisible = rowsToRender.length > 0;
	}

	return (
		<Stack xcss={propertiesSectionStyles}>
			{isInfoSectionTitleVisible && (
				<FormattedMessage {...i18n.propertiesSectionTitle} tagName="h4" />
			)}
			<Stack>{rowsToRender}</Stack>
		</Stack>
	);
};
